import * as React from 'react';
import {Button, Typography} from 'onboarding-components';
import clsx from 'clsx';
import moment from 'moment';
import copy from 'clipboard-copy';
import produce from 'immer';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {SectionReact} from 'common/components/section';
import {SvgIconReact} from 'common/components/svg-icon';
import {TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {reporter} from 'common/report/error-reporting';
import {appApiUrl} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';
import {Banner} from 'common/onboarding/immersive-onboarding/components/banner';
import {
  AppContext,
  CheckIntegrationResult,
  SetupGuideContext,
  Step,
  SubStep,
} from 'common/onboarding/immersive-onboarding/types';
import {scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import {useMountEffect} from 'common/onboarding/utils';
import {SetUpTrackingEvent, track, TrackingEvent, trackSetUp} from 'common/onboarding/immersive-onboarding/tracking';
import {ApiClient} from 'common/onboarding/immersive-onboarding/api-client';

import styles from './implement.module.styl';
import sectionStyleOverride from './section.inline.styl';

type ImplementMethodProps = {
  title: string;
  url: string;
  imgSrc: string;
  highlight?: string;
  hasMarginLeft?: boolean;
};

export const Implement = () => {
  const {completed, setupGuideInfo} = React.useContext(SetupGuideContext);
  const paths = window.location.pathname.split(`/`);
  const projectId = paths[paths.indexOf(`project`) + 1];

  const copyToken = async (token: string) => {
    try {
      await copy(token);
      addToast({
        type: ToastType.SUCCESS,
        message: `Token Copied!`,
      });
      trackSetUp(SetUpTrackingEvent.TokenCopied);
    } catch (err) {
      reporter.error(err);
      addToast({
        type: ToastType.ERROR,
        message: `Something went wrong copying the link`,
      });
    }
  };

  const ProjectToken = () => {
    if (!setupGuideInfo?.projectToken) {
      return null;
    }

    const handleCopyToken = () => {
      if (setupGuideInfo.projectToken) {
        copyToken(`${setupGuideInfo.projectToken}`);
      }
    };

    return (
      <div className={styles.tokenContainer}>
        {`Copy your project token: `}
        <span className={styles.token}>
          <code>{setupGuideInfo.projectToken?.slice(0, 15) + `…`}</code>
          {setupGuideInfo.projectToken && (
            <button className={styles.copyTokenButton} onClick={handleCopyToken}>
              <SvgIconReact icon={`copy`} />
            </button>
          )}
        </span>
      </div>
    );
  };

  const ImplementMethod = ({title, url, imgSrc, highlight, hasMarginLeft}: ImplementMethodProps) => {
    return (
      <a
        className={clsx(styles.implementMethod, {[styles.marginLeft]: hasMarginLeft})}
        href={url}
        target='_blank'
        onClick={() => {
          trackSetUp(SetUpTrackingEvent.LinkClicked, {cta: title});
        }}
      >
        <div className={styles.implementMethodIcon}>
          <img src={imgSrc} />
        </div>

        {highlight ? (
          <div className={styles.implementMethodTextContainer}>
            <Typography variant='captionXs' weight='bold' className={styles.implementMethodText}>
              {title}
            </Typography>
            <TextHighlightReact size='small' theme={TEXT_HIGHLIGHT_THEME.SUCCESS} text={highlight} />
          </div>
        ) : (
          <Typography variant='captionXs' weight='bold' className={styles.implementMethodText}>
            {title}
          </Typography>
        )}
      </a>
    );
  };

  const SectionWithMethods = ({children, headerTitle}) => (
    <SectionReact
      headerTitle={headerTitle}
      headerTitleLowercase
      isClosed={false}
      //@ts-ignore
      styleOverride={sectionStyleOverride}
    >
      {children}
    </SectionReact>
  );
  const eventList = (events) => {
    if (!events || events.length === 0) {
      return ``;
    }
    if (events.length === 1) {
      return events[0];
    }
    return events.slice(0, -1).join(`, `) + ` and ` + events.slice(-1);
  };

  const EventBanner = ({events, lastEditedUser, lastEditedDate}) => {
    return (
      <Banner isVisible={events.length > 0}>
        <Banner.Content
          title={`${events.length} planned events: ${eventList(events)}`}
          description={`Last edited by ${lastEditedUser} on ${lastEditedDate}`}
        />
        <Banner.Action
          action={() => {
            scrollToStep(SubStep.EventConfirmation, {skipAnimation: true});
            track(TrackingEvent.EditEvent);
          }}
          actionName={`Edit events`}
        />
      </Banner>
    );
  };

  const ImplementCdpCTA = () => {
    return (
      <>
        <div className={styles.implementSection}>
          <SectionWithMethods headerTitle='I already collect data with a CDP or Data Warehouse'>
            <div className={styles.implementMethods}>
              <div className={styles.implementMethodRow}>
                <ImplementMethod
                  title={`Segment`}
                  url={`https://developer.mixpanel.com/docs/segment`}
                  imgSrc={IMMERSIVE_ASSETS.segment}
                />
                <ImplementMethod
                  title={`RudderStack`}
                  url={`https://developer.mixpanel.com/docs/rudderstack`}
                  imgSrc={IMMERSIVE_ASSETS.rudderstack}
                  hasMarginLeft
                />
                <ImplementMethod
                  title={`Google Tag Manager`}
                  url={`https://docs.mixpanel.com/docs/tracking/integrations/google-tag-manager`}
                  imgSrc={IMMERSIVE_ASSETS.gtm}
                  hasMarginLeft
                />
              </div>
              <div className={styles.implementMethodRow}>
                <ImplementMethod
                  title={`Snowflake`}
                  url={`https://docs.mixpanel.com/docs/tracking-methods/data-warehouse/snowflake`}
                  imgSrc={IMMERSIVE_ASSETS.snowflake}
                  highlight={`Codeless`}
                />
                <ImplementMethod
                  title={`BigQuery`}
                  url={`https://docs.mixpanel.com/docs/tracking/data-warehouse/bigquery`}
                  imgSrc={IMMERSIVE_ASSETS.bq}
                  highlight={`Codeless`}
                  hasMarginLeft
                />
                <ImplementMethod
                  title={`More Options`}
                  url={`https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method`}
                  imgSrc={IMMERSIVE_ASSETS.more}
                  hasMarginLeft
                />
              </div>
              <div>
                <ProjectToken />
              </div>
            </div>
          </SectionWithMethods>
        </div>
      </>
    );
  };

  const ImplementSdkCTA = () => {
    return (
      <>
        <div className={styles.implementSection}>
          <SectionWithMethods headerTitle='I need to start tracking product data'>
            <div>
              <div className={styles.implementBodyContainer}>
                <div className={styles.implementBodyTitle}>{`We recommend you use a server-side implementation. `}</div>
                <span>
                  <a
                    href='https://developer.mixpanel.com/reference/client-side-vs-server-side-tracking'
                    target='_blank'
                  >
                    Learn more
                  </a>
                </span>
              </div>
              <div className={styles.implementMethods}>
                <div className={styles.implementMethodRow}>
                  <ImplementMethod
                    title={`JavaScript`}
                    url={`https://developer.mixpanel.com/docs/javascript-quickstart`}
                    imgSrc={IMMERSIVE_ASSETS.js}
                    highlight={`MostPopular`}
                  />
                  <ImplementMethod
                    title={`Android`}
                    url={`https://developer.mixpanel.com/docs/android-quickstart`}
                    imgSrc={IMMERSIVE_ASSETS.android}
                    hasMarginLeft
                  />
                  <ImplementMethod
                    title={`iOS Swift`}
                    url={`https://developer.mixpanel.com/docs/ios-swift-quickstart`}
                    imgSrc={IMMERSIVE_ASSETS.ios}
                    hasMarginLeft
                  />
                </div>
                <div className={styles.implementMethodRow}>
                  <ImplementMethod
                    title={`Objective-C`}
                    url={`https://developer.mixpanel.com/docs/ios-objective-c-quickstart`}
                    imgSrc={IMMERSIVE_ASSETS.ios}
                  />
                  <ImplementMethod
                    title={`More Options`}
                    url={`https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method`}
                    imgSrc={IMMERSIVE_ASSETS.more}
                    hasMarginLeft
                  />
                </div>
                <div>
                  <ProjectToken />
                </div>
              </div>
            </div>
          </SectionWithMethods>
        </div>
      </>
    );
  };

  const EventListener = ({isIntegrated}) => {
    return (
      <>
        <div className={styles.eventListenerContainer}>
          <div className={styles.eventListenerTitle}>
            <div className={styles.eventListenerImage}>
              {isIntegrated === true ? (
                <img src={IMMERSIVE_ASSETS.eventDetected} />
              ) : (
                <img src={IMMERSIVE_ASSETS.eventListenerGif} width='50' height='9' />
              )}
            </div>
            <Typography variant='bodySm' weight='bold'>
              {isIntegrated === true ? `Events detected!` : `Listening for events...`}
            </Typography>
          </div>
          <div className={styles.eventListenerDescription}>
            {isIntegrated === true ? (
              <div className={styles.eventCheckMarkImage}>
                <img src={IMMERSIVE_ASSETS.checkmark} />
              </div>
            ) : (
              ``
            )}
            <Typography variant='bodySm' weight='regular'>
              {isIntegrated === true ? `Last checked - Just now` : `No events detected - Just now`}
            </Typography>
          </div>
        </div>
      </>
    );
  };

  const SectionButton = ({title, onClick, disabled}) => {
    return (
      <div className={styles.implementFooterButton}>
        <Button
          disabled={disabled}
          onClick={() => {
            !disabled && onClick();
          }}
        >
          {title}
        </Button>
      </div>
    );
  };

  const ImplementCTA = () => {
    const {isIntegrated, setIsIntegrated, setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);

    const pollingRef = React.useRef(null);

    const getProjectIntegrationStatus = async () => {
      try {
        const res: CheckIntegrationResult = await apiClient.get(appApiUrl(`check-integration`, {projectId}));
        if (res.is_integrated) {
          if (setupGuideInfo) {
            const updatedStepsCompleted = produce(setupGuideInfo.stepsCompleted, (draft) => draft.add(Step.Implement));

            ApiClient.updateCompleted(setupGuideInfo.projectId, updatedStepsCompleted);

            setSetupGuideInfo({...setupGuideInfo, stepsCompleted: updatedStepsCompleted});
          }
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsIntegrated(res.is_integrated);
        trackSetUp(SetUpTrackingEvent.IntegratedWithinSession);
      } catch (error) {
        reporter.error(error);
      }
    };

    useMountEffect(() => {
      if (pollingRef.current === null && !isIntegrated) {
        pollingRef.current = window.setInterval(getProjectIntegrationStatus, 10000);
      }

      return () => {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      };
    });

    return (
      <div>
        <ImplementCdpCTA />
        <ImplementSdkCTA />
        <EventListener isIntegrated={isIntegrated} />
        <SectionButton
          disabled={!isIntegrated}
          title={`Continue`}
          onClick={() => {
            scrollToStep(Step.Explore);
          }}
        />
      </div>
    );
  };

  const {currentUser} = React.useContext(AppContext);

  return (
    <>
      <Header
        title={`Connect your data`}
        step={`step 2`}
        duration={15}
        imgSrc={IMMERSIVE_ASSETS.sendData}
        completed={completed[Step.Implement]}
      />
      <Content>
        <EventBanner
          events={setupGuideInfo?.events ?? []}
          lastEditedUser={
            setupGuideInfo?.email === currentUser.get().value?.email ? `you` : setupGuideInfo?.author ?? ``
          }
          lastEditedDate={setupGuideInfo?.modified ? moment(setupGuideInfo.modified).format(`MMM D, YYYY`) : ``}
        />
        <div className={styles.implementHeader}>
          <Typography className={styles.implementHeaderText} variant='bodySm'>
            {`You’re ready to start sending data. Choose the method below that best represents you:`}
          </Typography>
        </div>
        <ImplementCTA />
      </Content>
    </>
  );
};
